export interface ProcessVideoResult {
  success?: boolean;
  videoId: string;
  url?: string;
  pathParts?: string[];
  error?: string;
}

interface ElectronMediaBridge {
  envConfig?: {
    syncApiBaseUrl: string;
  };
  processVideo?: (
    file: File,
    pathParts: string[],
    onProgress?: (progress: number) => void,
  ) => Promise<ProcessVideoResult>;
  getResourceUrl?: (pathParts: string[], fileName?: string) => string;
  resolveResourceUrl?: (pathParts: string[], fileName?: string) => string;
  buildResourceUrl?: (pathParts: string[], fileName?: string) => string;
  resourceExists?: (pathParts: string[], fileName?: string) => Promise<boolean>;
  fileExists?: (pathParts: string[], fileName?: string) => Promise<boolean>;
  writeFile?: (
    pathParts: string[],
    fileName: string,
    data: Blob | string | File | Uint8Array | ArrayBuffer,
  ) => Promise<unknown>;
  downloadAndExtractZip?: (
    url: string,
    headers: Record<string, string>,
    extractDir: string[],
    mainEncId: string,
    onProgress?: (data: {
      stage: "downloading" | "extracting" | "completed";
      percent: number;
      downloadedBytes?: number;
      totalBytes?: number;
      extractedBytes?: number;
      totalExtractBytes?: number;
    }) => void,
  ) => Promise<{
    success: boolean;
    encFileName?: string;
    extractedFiles?: string[];
    error?: string;
  }>;
  downloadFile?: (
    url: string,
    headers: Record<string, string>,
    destDir: string[],
    fileName: string,
    onProgress?: (data: {
      stage: "downloading" | "completed";
      percent: number;
      downloadedBytes?: number;
      totalBytes?: number;
      fileName?: string;
    }) => void,
    expectedSize?: number,
  ) => Promise<{
    success: boolean;
    error?: string;
  }>;
  cancelDownload?: () => Promise<{ success: boolean; aborted?: boolean }>;
  prepareEncPlayback?: (
    encRelativePath: string,
    encryptionKey: string,
  ) => Promise<{
    success: boolean;
    sessionId?: string;
    url?: string;
    plaintextSize?: number;
    error?: string;
  }>;
  cleanupEncSession?: (sessionId: string) => Promise<{ success: boolean }>;
  clearResources?: () => Promise<boolean>;
  deleteResource?: (pathParts: string[]) => Promise<boolean>;
  openExternal?: (url: string) => Promise<{ success: boolean; error?: string }>;
  quitApp?: () => Promise<void>;
  hardReload?: () => void;
}

declare global {
  interface Window {
    electronApp?: ElectronMediaBridge;
  }
}

const DEFAULT_VIDEO_FILE = "index.m3u8";

const joinResourcePath = (pathParts: string[], fileName = DEFAULT_VIDEO_FILE) =>
  [...pathParts, fileName].join("/");

export const isElectronMediaRuntime = () =>
  typeof window !== "undefined" && !!window.electronApp;

/**
 * Builds an X-Forwarded-For value listing every local IPv4 reported by the
 * Electron `getIPInfo` bridge, preferred LAN IP first, so the backend can
 * CIDR-match the real NIC against campus subnets. Accepts both the
 * `allLanIPs` (comma-separated string) and `allLocalIPs` (array) shapes.
 */
export const buildForwardedForHeader = (ipInfo: any): string => {
  const ips: string[] = [];
  const add = (value: unknown) => {
    if (Array.isArray(value)) {
      value.forEach(add);
      return;
    }
    if (typeof value !== "string") return;
    value
      .split(",")
      .map((ip) => ip.trim())
      .filter((ip) => ip && ip !== "127.0.0.1" && !ips.includes(ip))
      .forEach((ip) => ips.push(ip));
  };
  add(ipInfo?.privateLanIP);
  add(ipInfo?.localIP);
  add(ipInfo?.allLanIPs);
  add(ipInfo?.allLocalIPs);
  return ips.join(",");
};

export const isFileProtocolRuntime = () =>
  typeof window !== "undefined" && window.location.protocol === "file:";

export const getResourceUrl = (
  pathParts: string[],
  fileName = DEFAULT_VIDEO_FILE,
) => {
  const bridge = window.electronApp;
  if (bridge?.getResourceUrl) {
    return bridge.getResourceUrl(pathParts, fileName);
  }
  if (bridge?.resolveResourceUrl) {
    return bridge.resolveResourceUrl(pathParts, fileName);
  }
  if (bridge?.buildResourceUrl) {
    return bridge.buildResourceUrl(pathParts, fileName);
  }
  return `/resource/${joinResourcePath(pathParts, fileName)}`;
};

export const resourceExists = async (
  pathParts: string[],
  fileName = DEFAULT_VIDEO_FILE,
) => {
  const bridge = window.electronApp;
  if (bridge?.resourceExists) {
    return bridge.resourceExists(pathParts, fileName);
  }
  if (bridge?.fileExists) {
    return bridge.fileExists(pathParts, fileName);
  }
  return null;
};

export const readBlobAsArrayBuffer = (blob: Blob): Promise<ArrayBuffer> => {
  if (!blob) {
    return Promise.reject(new Error("Invalid or empty blob provided"));
  }
  if (typeof blob.arrayBuffer === "function") {
    return blob.arrayBuffer();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
      } else {
        reject(new Error("Failed to read blob as ArrayBuffer"));
      }
    };
    reader.onerror = () => reject(reader.error || new Error("Error reading blob"));
    reader.readAsArrayBuffer(blob);
  });
};

export const writeResourceFile = async (
  pathParts: string[],
  fileName: string,
  data: Blob | string | File | Uint8Array | ArrayBuffer,
) => {
  const bridge = window.electronApp;
  if (!bridge?.writeFile) {
    return null;
  }
  // The Electron bridge's writeFile expects Blob | string | File.
  // It internally calls .arrayBuffer() on the data, so we must
  // convert Uint8Array / ArrayBuffer to a Blob before passing.
  let dataToSend: Blob | string | File;
  if (data instanceof Uint8Array) {
    dataToSend = new Blob([data]);
  } else if (data instanceof ArrayBuffer) {
    dataToSend = new Blob([new Uint8Array(data)]);
  } else {
    dataToSend = data as Blob | string | File;
  }
  return bridge.writeFile(pathParts, fileName, dataToSend);
};

export const clearElectronResources = async () => {
  const bridge = window.electronApp;
  if (!bridge?.clearResources) {
    return null;
  }
  return bridge.clearResources();
};

export const deleteElectronResource = async (pathParts: string[]) => {
  const bridge = window.electronApp;
  if (!bridge?.deleteResource) {
    return null;
  }
  return bridge.deleteResource(pathParts);
};

export const processVideoInElectron = async (
  file: File,
  pathParts: string[],
  onProgress?: (progress: number) => void,
) => {
  const bridge = window.electronApp;
  if (!bridge?.processVideo) {
    return null;
  }
  return bridge.processVideo(file, pathParts, onProgress);
};

/**
 * Opens a URL in the user's default browser if running in Electron,
 * otherwise opens in a new tab/window in the current browser.
 * @param url The URL to open
 */
export const openExternalUrl = (url: string) => {
  const bridge = window.electronApp;
  if (bridge?.openExternal) {
    bridge.openExternal(url);
  } else {
    window.open(url, "_blank");
  }
};

/**
 * Download a ZIP from a URL, extract it, and save files to disk — entirely
 * in the Electron main process (Node.js). This bypasses the browser's ~2 GB
 * ArrayBuffer limit. Returns null if not running in Electron or bridge is
 * unavailable.
 */
export type DownloadExtractProgress = {
  stage: "downloading" | "extracting" | "completed";
  percent: number;
  downloadedBytes?: number;
  totalBytes?: number;
};

export const electronDownloadAndExtractZip = async (
  url: string,
  headers: Record<string, string>,
  extractDir: string[],
  mainEncId: string,
  onProgress?: (data: DownloadExtractProgress) => void,
) => {
  const bridge = window.electronApp;
  if (!bridge?.downloadAndExtractZip) {
    return null;
  }
  return bridge.downloadAndExtractZip(url, headers, extractDir, mainEncId, onProgress);
};

/**
 * Download a single file from a URL directly to disk — entirely in the
 * Electron main process (Node.js). This bypasses the browser's ~2 GB
 * ArrayBuffer limit. Returns null if not running in Electron or bridge
 * is unavailable.
 */
export type DownloadFileProgress = {
  stage: "downloading" | "completed";
  percent: number;
  downloadedBytes?: number;
  totalBytes?: number;
  fileName?: string;
};

export const cancelElectronDownload = async () => {
  const bridge = window.electronApp;
  if (bridge?.cancelDownload) {
    try {
      return await bridge.cancelDownload();
    } catch (e) {
      console.warn("Failed to cancel Electron download:", e);
    }
  }
  return null;
};

export const electronDownloadFile = async (
  url: string,
  headers: Record<string, string>,
  destDir: string[],
  fileName: string,
  onProgress?: (data: DownloadFileProgress) => void,
  expectedSize?: number,
) => {
  const bridge = window.electronApp;
  if (!bridge?.downloadFile) {
    return null;
  }
  return bridge.downloadFile(
    url,
    headers,
    destDir,
    fileName,
    onProgress,
    expectedSize,
  );
};

/**
 * Prepare an encrypted .enc video for streaming playback in Electron.
 * The main process parses the file header and creates a session so the
 * tb:// protocol handler can decrypt byte ranges on-the-fly.
 * No plaintext video is ever written to disk.
 */
export const electronPrepareEncPlayback = async (
  encRelativePath: string,
  encryptionKey: string,
) => {
  const bridge = window.electronApp;
  if (!bridge?.prepareEncPlayback) {
    return null;
  }
  return bridge.prepareEncPlayback(encRelativePath, encryptionKey);
};

/**
 * Clean up a streaming encrypted playback session when the player
 * unmounts or navigates to a different video.
 */
export const electronCleanupEncSession = async (sessionId: string) => {
  const bridge = window.electronApp;
  if (!bridge?.cleanupEncSession) {
    return null;
  }
  return bridge.cleanupEncSession(sessionId);
};
